import React from 'react';
import { Card } from './UIComponents';
import { Calendar, CreditCard, XCircle, Crown } from 'lucide-react';
import type { Subscription } from '../types';

interface SubscriptionCardProps {
  subscription: Subscription;
  onCancel?: () => void;
  onUpgrade?: () => void;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function getStatusBadge(subscription: Subscription) {
  const { status, cancel_at_period_end } = subscription;

  if (cancel_at_period_end) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
        <XCircle className="h-3 w-3" />
        Cancels {formatDate(subscription.current_period_end)}
      </span>
    );
  }

  switch (status) {
    case 'active':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
          Active
        </span>
      );
    case 'trialing':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
          Trial
        </span>
      );
    case 'past_due':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
          Past Due
        </span>
      );
    case 'cancelled':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-zinc-800 text-slate-800 dark:text-zinc-300">
          Cancelled
        </span>
      );
    case 'expired':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-zinc-800 text-slate-800 dark:text-zinc-300">
          Expired
        </span>
      );
    default:
      return null;
  }
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  onCancel,
  onUpgrade,
}) => {
  const planName = subscription.plan?.name || subscription.plan_id;
  const isActive = subscription.status === 'active' || subscription.status === 'trialing';

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Crown className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{planName}</h3>
            {getStatusBadge(subscription)}
          </div>
          <p className="mt-1 text-sm text-slate-500 dark:text-zinc-400 capitalize">
            {subscription.billing_interval} billing
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
          <div>
            <p className="text-xs text-slate-500 dark:text-zinc-400">Current Period</p>
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <CreditCard className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
          <div>
            <p className="text-xs text-slate-500 dark:text-zinc-400">Next Billing</p>
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {subscription.cancel_at_period_end
                ? 'No renewal'
                : formatDate(subscription.current_period_end)}
            </p>
          </div>
        </div>
      </div>

      {isActive && !subscription.cancel_at_period_end && (
        <div className="mt-6 flex gap-3">
          {onUpgrade && (
            <button
              onClick={onUpgrade}
              className="flex-1 px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
            >
              Change Plan
            </button>
          )}
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-zinc-400 bg-slate-50 dark:bg-zinc-800 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-700 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {subscription.cancel_at_period_end && (
        <div className="mt-6 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            Your subscription will end on {formatDate(subscription.current_period_end)}.
            You can continue using the service until then.
          </p>
        </div>
      )}
    </Card>
  );
};

export default SubscriptionCard;
